import React, { FC, useCallback, useEffect, useState } from 'react';
import ArchyModal from 'components/shell/ArchyModal';
import { ui } from 'util/ui';
import { ArrowIcon, DownloadIcon } from 'components/shell/Icons';

/**
 * Gallery image viewer (Aaron, 5 Nov 2026): the selected image large, previous / next (buttons or the
 * arrow keys), and Save at the original size, 75%, 50%, 25% or as a thumbnail.
 *
 * Smaller sizes are made in the browser from the original. That needs the image's bucket to allow the
 * site to read it (CORS); where it doesn't, the original still opens in a new tab.
 */

export type ViewerImage = { key: string; name: string; url: string; note?: string };

type Props = {
  images: ViewerImage[];
  index: number;
  onIndex(i: number): void;
  onClose(): void;
};

const THUMB_EDGE = 320;
type SizeChoice = { key: string; label: string; scale?: number; thumb?: boolean };
const SIZES: SizeChoice[] = [
  { key: 'full', label: 'Original size', scale: 1 },
  { key: '75', label: '75%', scale: 0.75 },
  { key: '50', label: '50%', scale: 0.5 },
  { key: '25', label: '25%', scale: 0.25 },
  { key: 'thumb', label: `Thumbnail (${THUMB_EDGE}px)`, thumb: true },
];

const targetSize = (choice: SizeChoice, w: number, h: number) => {
  if (choice.thumb) {
    const s = Math.min(1, THUMB_EDGE / Math.max(w, h));
    return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
  }
  const s = choice.scale || 1;
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
};

const saveBlob = (blob: Blob, fileName: string) => {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 2000);
};

const splitName = (name: string, type: string) => {
  const m = name.match(/^(.*?)(\.[a-z0-9]{2,5})?$/i);
  const base = (m && m[1]) || 'image';
  const ext = (m && m[2]) || (type === 'image/png' ? '.png' : type === 'image/webp' ? '.webp' : '.jpg');
  return { base, ext };
};

const loadBitmap = async (blob: Blob): Promise<CanvasImageSource & { width: number; height: number }> => {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }
};

const ImageViewer: FC<Props> = ({ images, index, onIndex, onClose }) => {
  const image = images[index];
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const count = images.length;

  const go = useCallback(
    (step: number) => {
      if (count < 2) return;
      onIndex((index + step + count) % count);
    },
    [count, index, onIndex],
  );

  useEffect(() => {
    setDims(null);
    setMessage('');
  }, [index]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [go]);

  const save = async (choice: SizeChoice) => {
    if (!image) return;
    setBusy(choice.key);
    setMessage('');
    try {
      const res = await fetch(image.url, { mode: 'cors' });
      if (!res.ok) throw new Error(`The image server answered ${res.status}.`);
      const blob = await res.blob();
      const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const { base, ext } = splitName(image.name, type);
      if (choice.scale === 1) {
        saveBlob(blob, `${base}${ext}`);
      } else {
        const bmp = await loadBitmap(blob);
        const size = targetSize(choice, bmp.width, bmp.height);
        const canvas = document.createElement('canvas');
        canvas.width = size.w;
        canvas.height = size.h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('This browser could not resize the image.');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, 0, 0, size.w, size.h);
        const outType = type === 'image/png' ? 'image/png' : 'image/jpeg';
        const out: Blob | null = await new Promise((r) => canvas.toBlob(r, outType, 0.92));
        if (!out) throw new Error('This browser could not resize the image.');
        const suffix = choice.thumb ? '_thumbnail' : `_${choice.key}pct`;
        saveBlob(out, `${base}${suffix}${outType === 'image/png' ? '.png' : '.jpg'}`);
      }
      setMessage(`Saved ${choice.thumb ? 'a thumbnail' : choice.scale === 1 ? 'the original' : `a ${choice.label} copy`}.`);
    } catch (e) {
      // Most likely the bucket doesn't yet allow the site to read images (CORS): open the original instead.
      if (choice.scale === 1) {
        window.open(image.url, '_blank', 'noopener');
        setMessage('Opened the original in a new tab: right-click it and choose "Save image as".');
      } else {
        setMessage(`Smaller sizes aren't available for this image yet. ${(e as Error).message || ''}`.trim());
      }
    } finally {
      setBusy(null);
    }
  };

  if (!image) return null;
  const navBtn = (dir: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    [dir]: 12,
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(22,24,29,0.55)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  });

  return (
    <ArchyModal
      title={image.name}
      subtitle={`${count > 1 ? `${index + 1} of ${count}` : 'Gallery'}${image.note ? ` · ${image.note}` : ''}${dims ? ` · ${dims.w} × ${dims.h}` : ''}`}
      onClose={onClose}
      width={1000}
      media
    >
      <div
        style={{
          position: 'relative',
          background: '#000000',
          width: '100%',
          height: 'min(600px, calc(100vh - 260px))',
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          key={image.key}
          src={image.url}
          alt={image.name}
          onLoad={(e) => setDims({ w: (e.target as HTMLImageElement).naturalWidth, h: (e.target as HTMLImageElement).naturalHeight })}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
        {count > 1 && (
          <>
            <button type="button" className="archy-btn" aria-label="Previous image" title="Previous (←)" onClick={() => go(-1)} style={navBtn('left')}>
              <ArrowIcon dir="left" />
            </button>
            <button type="button" className="archy-btn" aria-label="Next image" title="Next (→)" onClick={() => go(1)} style={navBtn('right')}>
              <ArrowIcon dir="right" />
            </button>
          </>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '12px 16px', fontFamily: ui.font }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: ui.text2, marginRight: 4 }}>
          <DownloadIcon size={15} />
          Save
        </span>
        {SIZES.map((s) => {
          const size = dims ? targetSize(s, dims.w, dims.h) : null;
          return (
            <button
              key={s.key}
              type="button"
              className="archy-btn"
              disabled={busy !== null}
              onClick={() => save(s)}
              title={size ? `${size.w} × ${size.h} pixels` : undefined}
              style={{
                height: 34,
                padding: '0 12px',
                border: `1px solid ${s.scale === 1 ? ui.accent : ui.control}`,
                borderRadius: 10,
                background: s.scale === 1 ? ui.accent : ui.surface,
                color: s.scale === 1 ? '#FFFFFF' : ui.text2,
                font: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {busy === s.key ? 'Saving…' : s.label}
              {size && !s.thumb && s.scale !== 1 && <span style={{ fontWeight: 400, opacity: 0.75 }}> · {size.w}×{size.h}</span>}
            </button>
          );
        })}
        {message && (
          <span role="status" style={{ fontSize: 13, color: ui.muted, flex: '1 1 220px' }}>
            {message}
          </span>
        )}
      </div>
    </ArchyModal>
  );
};

export default ImageViewer;
